import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useErrorToast } from './queryUtils';
import {
  getTeamEvents,
  getTeamEvent,
  createTeamEvent,
  updateTeamEvent,
  deleteTeamEvent,
  publishTeamEvent,
  updateTeamEventScoped,
  cancelInstance,
  cancelSeries,
  endSeries,
  participate,
  unparticipate,
  declineEvent,
  addParticipant,
  setGuestCount,
  adminRemoveParticipant,
  markNoShow,
  getEventTypesDropdown,
  getOrganizerCandidates,
  getTeamEventPendingCount,
  uploadEventPhoto,
  deleteEventPhoto,
  getEventGallery,
  type CreateTeamEventInput,
  type UpdateTeamEventInput,
  type TeamEventScope,
} from '../services/api/teamEvents';

export function useTeamEvents() {
  return useQuery({
    queryKey: ['teamEvents'],
    queryFn: getTeamEvents,
  });
}

export function useTeamEventPendingCount(enabled = true) {
  return useQuery({
    queryKey: ['teamEventPendingCount'],
    queryFn: getTeamEventPendingCount,
    // Phase 93 WR-01 — callers on module-gated surfaces (Sidebar badge) pass
    // `hasModule('people')` so gated instances never fire a guaranteed-404
    // fetch. Default `true` preserves all other call sites.
    enabled,
  });
}

export function useTeamEvent(id: number | null) {
  return useQuery({
    queryKey: ['teamEvent', id],
    queryFn: () => getTeamEvent(id!),
    enabled: id !== null,
  });
}

export function useEventTypesDropdown() {
  return useQuery({
    queryKey: ['eventTypesDropdown'],
    queryFn: getEventTypesDropdown,
  });
}

export function useCreateTeamEvent() {
  const queryClient = useQueryClient();
  const onError = useErrorToast();
  return useMutation({
    mutationFn: (input: CreateTeamEventInput) => createTeamEvent(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['teamEvents'] });
    },
    onError: (err) => onError(err, "Couldn't create team event"),
  });
}

export function useUpdateTeamEvent() {
  const queryClient = useQueryClient();
  const onError = useErrorToast();
  return useMutation({
    mutationFn: ({ id, input }: { id: number; input: UpdateTeamEventInput }) =>
      updateTeamEvent(id, input),
    onSuccess: (_data, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['teamEvents'] });
      queryClient.invalidateQueries({ queryKey: ['teamEvent', id] });
    },
    onError: (err) => onError(err, "Couldn't update team event"),
  });
}

export function useDeleteTeamEvent() {
  const queryClient = useQueryClient();
  const onError = useErrorToast();
  return useMutation({
    mutationFn: (id: number) => deleteTeamEvent(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['teamEvents'] });
    },
    onError: (err) => onError(err, "Couldn't delete team event"),
  });
}

export function usePublishTeamEvent() {
  const queryClient = useQueryClient();
  const onError = useErrorToast();
  return useMutation({
    mutationFn: (id: number) => publishTeamEvent(id),
    onSuccess: (_data, id) => {
      queryClient.invalidateQueries({ queryKey: ['teamEvents'] });
      queryClient.invalidateQueries({ queryKey: ['teamEvent', id] });
      queryClient.invalidateQueries({ queryKey: ['teamEventPendingCount'] });
    },
    onError: (err) => onError(err, "Couldn't publish team event"),
  });
}

function useParticipationMutation(mutationFn: (eventId: number) => Promise<unknown>, errorMsg: string) {
  const queryClient = useQueryClient();
  const onError = useErrorToast();
  return useMutation({
    mutationFn,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['teamEvents'] });
      queryClient.invalidateQueries({ queryKey: ['teamEvent'] });
      queryClient.invalidateQueries({ queryKey: ['teamEventPendingCount'] });
      queryClient.invalidateQueries({ queryKey: ['organizerCandidates'] });
    },
    onError: (err) => onError(err, errorMsg),
  });
}

export function useParticipate() {
  return useParticipationMutation(participate, "Couldn't confirm participation");
}

export function useUnparticipate() {
  return useParticipationMutation(unparticipate, "Couldn't cancel participation");
}

export function useDecline() {
  return useParticipationMutation(declineEvent, "Couldn't decline event");
}

/**
 * MKS-533 — change the guests the signed-in user brings without touching their
 * RSVP status. Shares the participation invalidations so the headcount, the
 * list and the sidebar badge all refresh.
 */
export function useSetGuestCount() {
  const queryClient = useQueryClient();
  const onError = useErrorToast();
  return useMutation({
    mutationFn: ({ eventId, guestCount }: { eventId: number; guestCount: number }) =>
      setGuestCount(eventId, guestCount),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['teamEvents'] });
      queryClient.invalidateQueries({ queryKey: ['teamEvent'] });
    },
    onError: (err) => onError(err, "Couldn't update your guests"),
  });
}

export function useOrganizerCandidates(eventTypeId: number | null, eventDate?: string, eventId?: number) {
  return useQuery({
    queryKey: ['organizerCandidates', eventTypeId, eventDate, eventId],
    queryFn: () => getOrganizerCandidates(eventTypeId!, eventDate, eventId),
    enabled: eventTypeId !== null,
  });
}

export function useAddParticipant() {
  const queryClient = useQueryClient();
  const onError = useErrorToast();
  return useMutation({
    mutationFn: ({ eventId, userId, status }: { eventId: number; userId: number; status?: 'going' | 'not_going' }) =>
      addParticipant(eventId, userId, status),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['teamEvents'] });
      queryClient.invalidateQueries({ queryKey: ['teamEvent'] });
      // F6-009: admin-added participants affect the "needs RSVP" sidebar badge
      // for the added user, so the count must be refreshed.
      queryClient.invalidateQueries({ queryKey: ['teamEventPendingCount'] });
    },
    onError: (err) => onError(err, "Couldn't add participant"),
  });
}

export function useRemoveParticipant() {
  const queryClient = useQueryClient();
  const onError = useErrorToast();
  return useMutation({
    mutationFn: ({ eventId, userId }: { eventId: number; userId: number }) =>
      adminRemoveParticipant(eventId, userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['teamEvents'] });
      queryClient.invalidateQueries({ queryKey: ['teamEvent'] });
      // F6-009: removing a participant clears their pending RSVP, so the
      // sidebar badge count must refresh.
      queryClient.invalidateQueries({ queryKey: ['teamEventPendingCount'] });
    },
    onError: (err) => onError(err, "Couldn't remove participant"),
  });
}

export function useMarkNoShow() {
  const queryClient = useQueryClient();
  const onError = useErrorToast();
  return useMutation({
    mutationFn: ({ eventId, userId, noShow }: { eventId: number; userId: number; noShow: boolean }) =>
      markNoShow(eventId, userId, noShow),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['teamEvents'] });
      queryClient.invalidateQueries({ queryKey: ['teamEvent'] });
      queryClient.invalidateQueries({ queryKey: ['organizerCandidates'] });
      // F6-009: marking no-show resolves a pending RSVP, so the badge count
      // must refresh.
      queryClient.invalidateQueries({ queryKey: ['teamEventPendingCount'] });
    },
    onError: (err) => onError(err, "Couldn't update attendance"),
  });
}

function invalidateEventPhotos(queryClient: ReturnType<typeof useQueryClient>, eventId: number) {
  queryClient.invalidateQueries({ queryKey: ['teamEvent', eventId] });
  queryClient.invalidateQueries({ queryKey: ['eventPhotos', eventId] });
  queryClient.invalidateQueries({ queryKey: ['eventGallery'] });
}

export function useUploadEventPhoto() {
  const queryClient = useQueryClient();
  const onError = useErrorToast();
  return useMutation({
    mutationFn: ({ eventId, file }: { eventId: number; file: File }) =>
      uploadEventPhoto(eventId, file),
    onSuccess: (_data, { eventId }) => invalidateEventPhotos(queryClient, eventId),
    onError: (err) => onError(err, "Couldn't upload photo"),
  });
}

export function useDeleteEventPhoto() {
  const queryClient = useQueryClient();
  const onError = useErrorToast();
  return useMutation({
    mutationFn: ({ eventId, photoId }: { eventId: number; photoId: number }) =>
      deleteEventPhoto(eventId, photoId),
    onSuccess: (_data, { eventId }) => invalidateEventPhotos(queryClient, eventId),
    onError: (err) => onError(err, "Couldn't delete photo"),
  });
}

export function useEventGallery() {
  return useQuery({
    queryKey: ['eventGallery'],
    queryFn: getEventGallery,
  });
}

export function useUpdateEventScoped() {
  const queryClient = useQueryClient();
  const onError = useErrorToast();
  return useMutation({
    mutationFn: ({
      id,
      input,
      scope,
    }: {
      id: number;
      input: UpdateTeamEventInput;
      scope: TeamEventScope;
    }) => updateTeamEventScoped(id, input, scope),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['teamEvent', data.id] });
      queryClient.invalidateQueries({ queryKey: ['teamEvents'] });
    },
    onError: (err) => onError(err, "Couldn't update event"),
  });
}

export function useCancelInstance() {
  const queryClient = useQueryClient();
  const onError = useErrorToast();
  return useMutation({
    mutationFn: (id: number) => cancelInstance(id),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['teamEvent', data.id] });
      queryClient.invalidateQueries({ queryKey: ['teamEvents'] });
      // F6-009: cancelling an instance removes its pending RSVPs from the count.
      queryClient.invalidateQueries({ queryKey: ['teamEventPendingCount'] });
    },
    onError: (err) => onError(err, "Couldn't cancel event"),
  });
}

export function useCancelSeries() {
  const queryClient = useQueryClient();
  const onError = useErrorToast();
  return useMutation({
    mutationFn: (seriesId: number) => cancelSeries(seriesId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['teamEvents'] });
      // F6-009: cancelling a series removes future pending RSVPs from the count.
      queryClient.invalidateQueries({ queryKey: ['teamEventPendingCount'] });
    },
    onError: (err) => onError(err, "Couldn't cancel series"),
  });
}

export function useEndSeries() {
  const queryClient = useQueryClient();
  const onError = useErrorToast();
  return useMutation({
    mutationFn: ({ seriesId, endDate }: { seriesId: number; endDate: string }) =>
      endSeries(seriesId, endDate),
    onSuccess: (_data, { seriesId }) => {
      queryClient.invalidateQueries({ queryKey: ['teamEvent', seriesId] });
      queryClient.invalidateQueries({ queryKey: ['teamEvents'] });
      // F6-009: ending a series removes future pending RSVPs from the count.
      queryClient.invalidateQueries({ queryKey: ['teamEventPendingCount'] });
    },
    onError: (err) => onError(err, "Couldn't end series"),
  });
}
